import { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import {
  UseFieldArrayMove,
  UseFieldArrayRemove,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { isChartTag } from '../inputs/SidebarEditor';
import type {
  LayoutConfig,
  FormatOptions,
  TableStyleConfig,
  ChartConfig,
  KidDocumentForm,
  PdfTag,
  ChartTag,
} from '../../types/KidPdfGeneratorV2.types';
import type { KidTemplateType } from '../../../../../../api/kids/types';
import { MdHorizontalRule, MdOutlineWidthFull, MdTitle } from 'react-icons/md';
import {
  LuColumns2,
  LuColumns3,
} from 'react-icons/lu';
import { RiLayoutLeftLine, RiLayoutRightLine } from 'react-icons/ri';
import {
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaColumns,
  FaTable,
  FaChartBar,
} from 'react-icons/fa';
import { HiPencil } from 'react-icons/hi2';
import { BiSolidCustomize } from 'react-icons/bi';
import { LuLayoutTemplate } from "react-icons/lu";
import { RxPadding } from 'react-icons/rx';
import { MdTextFields, MdBarChart } from 'react-icons/md';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Tooltip from '@mui/material/Tooltip';
import LinkedSpacingControl from '../inputs/LinkedSpacingControl';
import TextBlockStyleControls from '../inputs/TextBlockStyleControls';
import AddBlockChooser from '../AddBlockChooser';
import {
  TEMPLATE_BACKGROUND_BY_TYPE,
  TEMPLATE_BORDER_BY_TYPE,
} from '../form/templateSelection.utils';
import { buildTemplateEditPath } from '../../../../utilities/TemplateManagement/templateManagement.helpers';
import { IconType } from 'react-icons/lib';

interface BlockInputWrapperProps {
  children: React.ReactNode;
  remove: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  index: number;
  fieldsLength: number;
  addField: (type: PdfTag, index: number, layout?: LayoutConfig) => number;
  isComplete?: boolean;
  /** Template ids that are invalid/inconsistent for this document (should be removed). */
  invalidTemplateIds?: Set<string>;
}

type LayoutPreset = {
  id: string;
  name: string;
  icon: IconType;
  layout?: LayoutConfig;
  tag: string;
};

export const TEXT_LAYOUT_PRESETS: LayoutPreset[] = [
  {
    id: 'full',
    name: 'Full',
    icon: MdOutlineWidthFull,
    tag: 'text',
  },
  {
    id: '2-col',
    name: '2 Columns',
    icon: LuColumns2,
    layout: { fixed_width: [50, 50] },
    tag: 'text',
  },
  {
    id: '3-col',
    name: '3 Columns',
    icon: LuColumns3,
    layout: { fixed_width: [33, 33, 34] },
    tag: 'text',
  },
  {
    id: 'wide-left',
    name: 'Wide Left',
    icon: RiLayoutRightLine,
    layout: { fixed_width: [70, 30] },
    tag: 'text',
  },
  {
    id: 'wide-right',
    name: 'Wide Right',
    icon: RiLayoutLeftLine,
    layout: { fixed_width: [30, 70] },
    tag: 'text',
  },
  {
    id: 'custom-ratio',
    name: 'Custom ratio',
    icon: BiSolidCustomize,
    layout: undefined, // Special marker for custom
    tag: 'text',
  },
];

export const BlockInputWrapper: FunctionComponent<BlockInputWrapperProps> = ({
  children,
  index,
  remove,
  move,
  fieldsLength,
  addField,
  isComplete = false,
  invalidTemplateIds,
}) => {
  const { control, setValue } = useFormContext<KidDocumentForm>();
  const total = fieldsLength;
  // Watch current layout and tag
  const currentLayout = useWatch({
    name: `elements.${index}.layout`,
    control,
  }) as LayoutConfig | undefined;

  const currentFixedWidth = currentLayout?.fixed_width;

  const formatOptions = useWatch({
    name: `elements.${index}.format_options`,
    control,
  }) as FormatOptions | undefined;

  const tableFormatOptions = useWatch({
    name: `elements.${index}.format_options`,
    control,
  }) as TableStyleConfig | undefined;

  const chartFormatOptions = useWatch({
    name: `elements.${index}.format_options`,
    control,
  }) as ChartConfig | undefined;

  const currentTag = useWatch({
    name: `elements.${index}.tag`,
    control,
  }) as PdfTag | undefined;
  const currentElement = useWatch({
    name: `elements.${index}` as const,
    control,
  }) as (KidDocumentForm['elements'][number])

  const lastUpdatedBy = currentElement?.template?.template_last_updated_by_name;

  // Get display name and color for block type
  const getBlockTypeInfo = (tag: PdfTag | undefined) => {
    const typeMap: {
      [K in Exclude<PdfTag, 'benchmark_chart'>]: {
        label: string;
        color: string;
        borderColor: string;
        icon: IconType;
      };
    } = {
      text: {
        label: 'Text',
        color: 'bg-blue-500',
        borderColor: 'border-blue-300',
        icon: MdTextFields,
      },
      heading: {
        label: 'Heading',
        color: 'bg-cyan-500',
        borderColor: 'border-cyan-300',
        icon: MdTitle,
      },
      sri_chart: {
        label: 'SRI',
        color: 'bg-green-500',
        borderColor: 'border-green-300',
        icon: MdBarChart,
      },
      table: {
        label: 'Table',
        color: 'bg-purple-500',
        borderColor: 'border-purple-300',
        icon: FaTable,
      },
      costs_over_time_table: {
        label: 'Costs Over Time Table',
        color: 'bg-purple-500',
        borderColor: 'border-purple-300',
        icon: FaTable,
      },
      pie_chart: {
        label: 'Pie Chart',
        color: 'bg-pink-500',
        borderColor: 'border-pink-300',
        icon: FaChartBar,
      },
      line_chart: {
        label: 'Line Chart',
        color: 'bg-orange-500',
        borderColor: 'border-orange-300',
        icon: FaChartBar,
      },
      bar_chart: {
        label: 'Bar Chart',
        color: 'bg-yellow-500',
        borderColor: 'border-yellow-300',
        icon: FaChartBar,
      },
      page_break: {
        label: 'Page Break',
        color: 'bg-slate-500',
        borderColor: 'border-slate-300',
        icon: MdTextFields,
      },
      divider: {
        label: 'Divider',
        color: 'bg-gray-500',
        borderColor: 'border-gray-300',
        icon: MdHorizontalRule,
      },
    };
    const validTag =
      tag && tag in typeMap ? (tag as keyof typeof typeMap) : 'text';
    return typeMap[validTag];
  };

  const handleStyleChange = (
    key: keyof FormatOptions,
    value: string | number | boolean,
  ) => {
    const currentStyles = (formatOptions || {}) as Record<string, any>;
    const updatedStyles: Record<string, any> = {
      ...currentStyles,
      [key]:
        typeof value === 'string' && !isNaN(Number(value))
          ? Number(value)
          : value,
    };
    setValue(`elements.${index}.format_options`, updatedStyles as any);
  };

  // Check if table has custom styling changes
  const hasTableCustomChanges = () => {
    if (!tableFormatOptions) return false;
    const opts = tableFormatOptions;

    // Check for non-default values
    const hasFontSize = opts.fontSize && opts.fontSize !== 12;
    const hasTextColor = opts.textColor && opts.textColor !== '#000000';
    const hasAlignment = opts.alignment && opts.alignment !== 'LEFT';
    const hasValign = opts.valign && opts.valign !== 'BOTTOM';
    const hasPadding =
      (opts.leftPadding && opts.leftPadding !== 0) ||
      (opts.rightPadding && opts.rightPadding !== 0) ||
      (opts.topPadding && opts.topPadding !== 0) ||
      (opts.bottomPadding && opts.bottomPadding !== 0);
    const hasBackground = opts.background && opts.background !== '#ffffff';
    const hasRowBackgrounds =
      opts.rowBackgrounds &&
      (opts.rowBackgrounds[0] !== '#ffffff' ||
        opts.rowBackgrounds[1] !== '#f0f0f0');
    const hasBorderRadius = opts.borderRadius && opts.borderRadius !== 0;
    const hasShowGrid = opts.showGrid !== undefined && opts.showGrid !== true;
    const hasHeaderFontSize = opts.headerFontSize && opts.headerFontSize !== 12;
    const hasHeaderTextColor =
      opts.headerTextColor && opts.headerTextColor !== '#000000';
    const hasHeaderBackground =
      opts.headerBackground && opts.headerBackground !== '#e0e0e0';
    const hasHeaderAlignment =
      opts.headerAlignment && opts.headerAlignment !== 'LEFT';

    return (
      hasFontSize ||
      hasTextColor ||
      hasAlignment ||
      hasValign ||
      hasPadding ||
      hasBackground ||
      hasRowBackgrounds ||
      hasBorderRadius ||
      hasShowGrid ||
      hasHeaderFontSize ||
      hasHeaderTextColor ||
      hasHeaderBackground ||
      hasHeaderAlignment
    );
  };

  // Reset all table styling values to defaults
  const resetTableStyling = () => {
    const defaultValues: Record<string, any> = {
      fontSize: 12,
      textColor: '#000000',
      alignment: 'LEFT',
      valign: 'BOTTOM',
      leftPadding: 0,
      rightPadding: 0,
      topPadding: 0,
      bottomPadding: 0,
      background: '#ffffff',
      rowBackgrounds: ['#ffffff', '#f0f0f0'],
      borderRadius: 0,
      showGrid: true,
      headerFontSize: 12,
      headerTextColor: '#000000',
      headerBackground: '#e0e0e0',
      headerAlignment: 'LEFT',
    };

    // Get current format options and remove styling properties
    const currentOptions = (tableFormatOptions || {}) as Record<string, any>;
    const updatedOptions: Record<string, any> = { ...currentOptions };

    // Remove all styling properties
    Object.keys(defaultValues).forEach((key) => {
      delete updatedOptions[key];
    });

    // If there are no other options left, set to undefined, otherwise keep other options
    const hasOtherOptions = Object.keys(updatedOptions).some(
      (key) => !Object.keys(defaultValues).includes(key),
    );

    setValue(
      `elements.${index}.format_options`,
      hasOtherOptions ? updatedOptions : undefined,
    );
  };

  // Handle table style change
  const handleTableStyleChange = (key: keyof TableStyleConfig, value: any) => {
    const currentStyles = (tableFormatOptions || {}) as Record<string, any>;
    const updatedStyles: Record<string, any> = {
      ...currentStyles,
      [key]: value,
    };
    setValue(`elements.${index}.format_options`, updatedStyles as any);
  };

  // Check if chart has custom styling changes
  const hasChartCustomChanges = () => {
    if (!chartFormatOptions) return false;
    const opts = chartFormatOptions;

    // Check for non-default values
    const hasTitle = opts.title && opts.title !== '';
    const hasFontSize = opts.fontSize && opts.fontSize !== 12;
    const hasBackgroundColor =
      opts.backgroundColor && opts.backgroundColor !== 'rgba(0,0,0,0)';
    const hasColor = opts.color && opts.color !== '#000000';
    const hasBarMode = opts.barmode && opts.barmode !== 'group';
    const hasHorizontal =
      opts.horizontal !== undefined && opts.horizontal !== false;
    const hasShowLegend =
      opts.showLegend !== undefined && opts.showLegend !== true;
    const hasLegendPosition =
      opts.legendPosition && opts.legendPosition !== 'top';
    const hasShowGridX =
      opts.showGridX !== undefined && opts.showGridX !== true;
    const hasShowGridY =
      opts.showGridY !== undefined && opts.showGridY !== true;
    const hasXaxisTitle = opts.xaxisTitle && opts.xaxisTitle !== '';
    const hasYaxisTitle = opts.yaxisTitle && opts.yaxisTitle !== '';
    const hasXaxisTickformat =
      opts.xaxisTickformat && opts.xaxisTickformat !== '';
    const hasXaxisTicksuffix =
      opts.xaxisTicksuffix && opts.xaxisTicksuffix !== '';
    const hasYaxisTickformat =
      opts.yaxisTickformat && opts.yaxisTickformat !== '';
    const hasYaxisTicksuffix =
      opts.yaxisTicksuffix && opts.yaxisTicksuffix !== '';
    const hasColorsList =
      opts.colorsList &&
      opts.colorsList.length > 0 &&
      opts.colorsList.some((c) => c !== '#1f77b4');

    return (
      hasTitle ||
      hasFontSize ||
      hasBackgroundColor ||
      hasColor ||
      hasBarMode ||
      hasHorizontal ||
      hasShowLegend ||
      hasLegendPosition ||
      hasShowGridX ||
      hasShowGridY ||
      hasXaxisTitle ||
      hasYaxisTitle ||
      hasXaxisTickformat ||
      hasXaxisTicksuffix ||
      hasYaxisTickformat ||
      hasYaxisTicksuffix ||
      hasColorsList
    );
  };

  // Reset all chart styling values to defaults
  const resetChartStyling = () => {
    const defaultValues: Record<string, any> = {
      title: '',
      fontSize: 12,
      backgroundColor: 'rgba(0,0,0,0)',
      color: '#000000',
      barmode: 'group',
      horizontal: false,
      showLegend: true,
      legendPosition: 'top',
      showGridX: true,
      showGridY: true,
      xaxisTitle: '',
      yaxisTitle: '',
      xaxisTickformat: '',
      xaxisTicksuffix: '',
      yaxisTickformat: '',
      yaxisTicksuffix: '',
      colorsList: [],
    };

    // Get current format options and remove styling properties
    const currentOptions = (chartFormatOptions || {}) as Record<string, any>;
    const updatedOptions: Record<string, any> = { ...currentOptions };

    // Remove all styling properties
    Object.keys(defaultValues).forEach((key) => {
      delete updatedOptions[key];
    });

    // If there are no other options left, set to undefined, otherwise keep other options
    const hasOtherOptions = Object.keys(updatedOptions).some(
      (key) => !Object.keys(defaultValues).includes(key),
    );

    setValue(
      `elements.${index}.format_options`,
      hasOtherOptions ? updatedOptions : undefined,
    );
  };

  // Handle chart style change
  const handleChartStyleChange = (key: keyof ChartConfig, value: any) => {
    const currentStyles = (chartFormatOptions || {}) as Record<string, any>;
    const updatedStyles: Record<string, any> = {
      ...currentStyles,
      [key]: value,
    };
    setValue(`elements.${index}.format_options`, updatedStyles as any);
  };

  // Check if current layout matches any preset (ignore non-essential props)
  const getMatchingPreset = () => {
    if ((currentLayout as any)?.custom) {
      return TEXT_LAYOUT_PRESETS.find((p) => p.id === 'custom-ratio');
    }

    const arraysEqual = (a?: number[], b?: number[]) => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    };

    const match = TEXT_LAYOUT_PRESETS.find((preset) => {
      const presetFixed = preset.layout?.fixed_width;
      const currentFixed = (currentLayout as any)?.fixed_width;
      if (!preset.layout && !currentLayout) return true; // Full preset when no layout
      if (!preset.layout) return false; // preset is Full but we have a layout
      return arraysEqual(currentFixed, presetFixed);
    });

    if (match) return match;
    if (currentFixedWidth && currentFixedWidth.length > 0) {
      return TEXT_LAYOUT_PRESETS.find((p) => p.id === 'custom-ratio');
    }
    return TEXT_LAYOUT_PRESETS[0];
  };

  const handlePresetSelect = (preset: LayoutPreset) => {
    // Don't change the tag, only update the layout
    if (preset.id === 'custom-ratio') {
      const next =
        currentFixedWidth && currentFixedWidth.length > 0
          ? {
            ...(currentLayout as any),
            fixed_width: currentFixedWidth,
            custom: true,
          }
          : { fixed_width: [50, 50], custom: true };
      setValue(`elements.${index}.layout`, next as any);
      return;
    }

    // For non-custom presets, apply exact layout (no custom flag)
    if (preset.layout) {
      setValue(`elements.${index}.layout`, preset.layout as any);
    } else {
      setValue(`elements.${index}.layout`, undefined);
    }
  };

  const handleManualLayoutChange = (colIndex: number, value: number) => {
    if (!currentFixedWidth) return;

    const updated = [...currentFixedWidth];
    const oldValue = updated[colIndex];
    const change = value - oldValue;

    if (change === 0) return;

    updated[colIndex] = value;

    if (updated.length === 1) {
      setValue(`elements.${index}.layout`, undefined);
      return;
    }

    const otherColumns = updated.filter((_, i) => i !== colIndex);
    const totalOtherColumns = otherColumns.reduce((sum, val) => sum + val, 0);

    if (totalOtherColumns === 0) {
      const remainingColumns = updated.length - 1;
      const evenDistribution = Math.floor((100 - value) / remainingColumns);
      const remainder = (100 - value) % remainingColumns;

      for (let i = 0; i < updated.length; i++) {
        if (i !== colIndex) {
          updated[i] =
            evenDistribution + (i < colIndex + 1 + remainder ? 1 : 0);
        }
      }
    } else {
      const newTotalOther = 100 - value;
      const ratio = newTotalOther / totalOtherColumns;

      for (let i = 0; i < updated.length; i++) {
        if (i !== colIndex) {
          updated[i] = Math.max(1, Math.round(updated[i] * ratio));
        }
      }

      const total = updated.reduce((sum, val) => sum + val, 0);
      const diff = 100 - total;
      if (diff !== 0) {
        for (let i = updated.length - 1; i >= 0; i--) {
          if (i !== colIndex) {
            updated[i] += diff;
            break;
          }
        }
      }
    }

    const newLayout = {
      ...(currentLayout as any),
      fixed_width: updated,
      custom: true,
    };
    setValue(`elements.${index}.layout`, newLayout);
  };

  const addColumnRatio = () => {
    if (!currentFixedWidth) return;

    const currentTotal = currentFixedWidth.reduce((sum, val) => sum + val, 0);
    const newColumnValue = Math.max(
      1,
      Math.floor(100 / (currentFixedWidth.length + 1)),
    );

    const updated = [...currentFixedWidth, newColumnValue];

    // Redistribute the remaining space proportionally among existing columns
    const remainingSpace = 100 - newColumnValue;
    const existingTotal = currentTotal;

    if (existingTotal > 0) {
      const ratio = remainingSpace / existingTotal;
      for (let i = 0; i < currentFixedWidth.length; i++) {
        updated[i] = Math.max(1, Math.round(updated[i] * ratio));
      }
    }

    // Fix rounding errors
    const total = updated.reduce((sum, val) => sum + val, 0);
    const diff = 100 - total;
    if (diff !== 0) {
      updated[updated.length - 1] += diff; // Adjust the new column
    }

    const newLayout = {
      ...(currentLayout as any),
      fixed_width: updated,
      custom: true,
    };
    setValue(`elements.${index}.layout`, newLayout);
  };

  const removeColumnRatio = (colIndex: number) => {
    if (!currentFixedWidth || currentFixedWidth.length <= 1) return;

    const removedValue = currentFixedWidth[colIndex];
    const updated = currentFixedWidth.filter((_, i) => i !== colIndex);

    // If only one column remains, remove layout and set to "Full"
    if (updated.length === 1) {
      setValue(`elements.${index}.layout`, undefined);
      return;
    }

    // Redistribute the removed value proportionally among remaining columns
    const remainingTotal = updated.reduce((sum, val) => sum + val, 0);

    if (remainingTotal > 0) {
      const ratio = (remainingTotal + removedValue) / remainingTotal;
      for (let i = 0; i < updated.length; i++) {
        updated[i] = Math.max(1, Math.round(updated[i] * ratio));
      }
    } else {
      // If remaining columns are all 0, distribute evenly
      const evenValue = Math.floor(100 / updated.length);
      const remainder = 100 % updated.length;

      for (let i = 0; i < updated.length; i++) {
        updated[i] = evenValue + (i < remainder ? 1 : 0);
      }
    }

    // Fix rounding errors
    const total = updated.reduce((sum, val) => sum + val, 0);
    const diff = 100 - total;
    if (diff !== 0) {
      updated[updated.length - 1] += diff;
    }

    const newLayout = {
      ...(currentLayout as any),
      fixed_width: updated,
      custom: true,
    };
    setValue(`elements.${index}.layout`, newLayout);
  };

  const templateBadgeStyles = currentElement?.template && currentElement?.template?.template_type
    ? {
      backgroundColor:
        TEMPLATE_BACKGROUND_BY_TYPE[currentElement?.template?.template_type as KidTemplateType],
      borderColor:
        TEMPLATE_BORDER_BY_TYPE[currentElement?.template?.template_type as KidTemplateType],
      color: '#1f2937',
    }
    : null;

  const isInvalidTemplate = Boolean(
    currentElement?.template_id &&
    invalidTemplateIds?.has(currentElement.template_id),
  );

  return (
    <div>
      <div
        className={`relative mb-4 rounded border-2 px-2 py-1 shadow-sm ${isInvalidTemplate
            ? 'border-rose-500 bg-rose-50'
            : 'border-gray-300 bg-white'
          }`}
      >
        {isInvalidTemplate && (
          <div className="mb-2 rounded-md border border-rose-300 bg-rose-100 px-2 py-1.5">
            <div className="text-xs font-semibold text-rose-900">
              This template should not be here
            </div>
            <p className="mt-0.5 text-[11px] leading-snug text-rose-800">
              It is invalid or inconsistent with the active template. Resolve the
              issue in Template Management.
            </p>
          </div>
        )}
        {/* Header Section */}
        <div className="mb-2 flex items-center justify-between border-b border-gray-200 pb-2">
          <div className="flex items-center gap-2">
            {/* Block Type Display (Read-only) */}
            {currentTag &&
              (() => {
                const typeInfo = getBlockTypeInfo(currentTag);
                const IconComponent = typeInfo.icon;
                return (
                  <div
                    className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold shadow-sm ${currentElement?.template_id ? 'border' : `border-0 text-white ${typeInfo.color}`}`}
                    style={templateBadgeStyles ?? undefined}
                  >
                    {currentElement?.template_id ? (
                      <LuLayoutTemplate className="h-4 w-4" />
                    ) : (
                      <IconComponent className="h-4 w-4" />
                    )}
                    <span>
                      {currentElement?.template_id && currentElement?.template?.template_type
                        ? currentElement?.template?.template_type.replace(/_/g, ' ').toUpperCase()
                        : typeInfo.label}
                    </span>
                  </div>
                );
              })()}
            {lastUpdatedBy ? (
              <span className="text-xs font-medium text-gray-500">
                Last updated by {lastUpdatedBy} <br /> {currentElement?.template?.template_updating_datetime ? new Date(currentElement?.template?.template_updating_datetime).toLocaleString() : ''}
              </span>
            ) : null}
          </div>
          {/* Action Icons */}
          <div className="flex items-center gap-1">
            {currentElement?.template_id ? (
              <Tooltip title="Edit template" arrow placement="top">
                <Link
                  to={buildTemplateEditPath(currentElement.template_id)}
                  aria-label="Edit template"
                  className="inline-flex rounded bg-mainblue p-1 text-white hover:bg-mainblue_hover"
                >
                  <HiPencil size={12} />
                </Link>
              </Tooltip>
            ) : null}
            <button
              type="button"
              onClick={() => move(index, index - 1)}
              disabled={index === 0}
              title="Move up"
              className="rounded bg-gray-200 p-1 text-xs text-gray-700 hover:bg-gray-300 disabled:opacity-30"
            >
              <FaChevronUp size={12} />
            </button>
            <button
              type="button"
              onClick={() => move(index, index + 1)}
              disabled={index === total - 1}
              title="Move down"
              className="rounded bg-gray-200 p-1 text-xs text-gray-700 hover:bg-gray-300 disabled:opacity-30"
            >
              <FaChevronDown size={12} />
            </button>
            <button
              type="button"
              onClick={() => remove(index)}
              disabled={Boolean(currentElement?.template_id)}
              title={
                currentElement?.template_id
                  ? 'Template blocks cannot be removed'
                  : 'Delete field'
              }
              className="rounded bg-red-500 p-1 text-xs text-white hover:bg-red-600 disabled:opacity-30"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>

        {/* Layout Toolbar with Controls */}
        {!currentElement?.template_id &&
          currentTag !== 'page_break' &&
          currentTag !== 'heading' && (
            <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
              {/* Layout Option - Available for all tags */}
              <Popover className="relative">
                {(() => {
                  const selectedPreset =
                    getMatchingPreset() || TEXT_LAYOUT_PRESETS[0];
                  const TriggerIcon = selectedPreset?.icon || FaColumns;
                  const label = selectedPreset?.name || 'Layout';
                  return (
                    <Tooltip
                      title={`Layout: ${label}`}
                      arrow
                      placement="bottom"
                      slotProps={{
                        arrow: {
                          className: 'text-mainblue',
                        },
                      }}
                    >
                      <PopoverButton
                        className={`rounded border border-gray-200 p-2 text-gray-600 transition-colors hover:border-blue-500 hover:bg-gray-100 hover:text-blue-600 focus:outline-none`}
                        aria-label={`Layout presets - ${label}`}
                      >
                        <TriggerIcon size={18} />
                      </PopoverButton>
                    </Tooltip>
                  );
                })()}
                <PopoverPanel className="absolute left-0 z-10 mt-2 rounded-md border border-gray-200 bg-white p-2 shadow-xl">
                  <div className="flex items-center gap-1">
                    {TEXT_LAYOUT_PRESETS.map((preset: LayoutPreset) => {
                      const IconComponent = preset.icon;
                      const isSelected =
                        getMatchingPreset()?.name === preset.name;
                      return (
                        <Tooltip
                          key={preset.name}
                          title={preset.name}
                          arrow
                          placement="bottom"
                        >
                          <button
                            type="button"
                            onClick={() => handlePresetSelect(preset)}
                            className={`flex items-center rounded-md border p-2 text-sm transition-colors ${isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                              }`}
                            aria-label={preset.name}
                          >
                            <IconComponent className="text-base" />
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                  {/* Custom Ratio Editor - Only show when Custom ratio preset is selected */}
                  {getMatchingPreset()?.id === 'custom-ratio' &&
                    currentFixedWidth &&
                    currentFixedWidth.length > 0 && (
                      <div className="mt-2 border-t border-gray-200 pt-2">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-600">
                            Custom ratio
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              className="rounded bg-blue-600 px-2 py-0.5 text-xs text-white"
                              onClick={addColumnRatio}
                              title="Add column"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {currentFixedWidth.map((val, colIndex) => (
                            <div
                              key={colIndex}
                              className="flex items-center gap-1"
                            >
                              <input
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                className="w-14 rounded border border-gray-300 p-1 text-[11px]"
                                value={val}
                                onChange={(e) => {
                                  const newValue = Math.max(
                                    1,
                                    Math.min(100, Number(e.target.value) || 1),
                                  );
                                  handleManualLayoutChange(colIndex, newValue);
                                }}
                              />
                              <span className="text-[11px] text-gray-500">%</span>
                              {currentFixedWidth.length > 1 && (
                                <button
                                  type="button"
                                  className="rounded px-1 text-[11px] text-red-600 hover:bg-red-50"
                                  title="Remove column"
                                  onClick={() => removeColumnRatio(colIndex)}
                                >
                                  ✖
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </PopoverPanel>
              </Popover>

              {/* Text Block Controls */}
              {currentTag === 'text' && (
                <TextBlockStyleControls
                  formatOptionsPath={`elements.${index}.format_options`}
                  idPrefix={index}
                />
              )}

              {/* Table Block Controls */}
              {currentTag === 'table' && (
                <>
                  {/* Table Styling & Appearance */}
                  <Popover className="relative">
                    <Tooltip
                      title="Spacing & Appearance"
                      arrow
                      placement="bottom"
                      slotProps={{
                        arrow: {
                          className: 'text-mainblue',
                        },
                      }}
                    >
                      <PopoverButton
                        className={`relative rounded border border-gray-200 p-2 text-gray-600 transition-colors hover:border-blue-500 hover:bg-gray-100 hover:text-blue-600 focus:outline-none ${hasTableCustomChanges()
                          ? 'border-blue-400 bg-blue-50'
                          : ''
                          }`}
                        aria-label={`Table styling & appearance`}
                      >
                        <RxPadding size={18} />
                        {hasTableCustomChanges() && (
                          <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-white" />
                        )}
                      </PopoverButton>
                    </Tooltip>
                    <PopoverPanel className="absolute left-0 z-10 mt-2 max-h-[600px] w-[500px] overflow-y-auto rounded-md border border-gray-200 bg-white p-3 text-xs shadow-xl">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="font-semibold text-gray-600">
                          Styling & Appearance
                        </div>
                        {hasTableCustomChanges() && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              resetTableStyling();
                            }}
                            className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:border-red-300 hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                            aria-label="Reset table styling"
                            title="Reset all table styling"
                          >
                            <FaTimes size={12} />
                            <span>Clear all</span>
                          </button>
                        )}
                      </div>

                      {/* Cell Styling and Header Row Styling - 2x2 Grid */}
                      <div className="grid grid-cols-2 gap-2 pb-2">
                        {/* Cell Styling */}
                        <div className="rounded-md border border-gray-200 p-2">
                          <div className="mb-3 text-xs font-semibold text-gray-700">
                            Cell Styling
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Font Size
                              </label>
                              <input
                                type="number"
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={Number(tableFormatOptions?.fontSize ?? 12)}
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'fontSize',
                                    Number(e.target.value || 12),
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Text Color
                              </label>
                              <input
                                type="color"
                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                value={tableFormatOptions?.textColor ?? '#000000'}
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'textColor',
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Alignment
                              </label>
                              <select
                                value={tableFormatOptions?.alignment ?? 'LEFT'}
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'alignment',
                                    e.target.value,
                                  )
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="LEFT">Left</option>
                                <option value="CENTER">Center</option>
                                <option value="RIGHT">Right</option>
                                <option value="DECIMAL">Decimal</option>
                              </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Vertical Align
                              </label>
                              <select
                                value={tableFormatOptions?.valign ?? 'BOTTOM'}
                                onChange={(e) =>
                                  handleTableStyleChange('valign', e.target.value)
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="TOP">Top</option>
                                <option value="MIDDLE">Middle</option>
                                <option value="BOTTOM">Bottom</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Header Row Styling */}
                        <div className="rounded-md border border-gray-200 p-2">
                          <div className="mb-3 text-xs font-semibold text-gray-700">
                            Header Row Styling
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Font Size
                              </label>
                              <input
                                type="number"
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={Number(
                                  tableFormatOptions?.headerFontSize ?? 12,
                                )}
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'headerFontSize',
                                    Number(e.target.value || 12),
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Text Color
                              </label>
                              <input
                                type="color"
                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                value={
                                  tableFormatOptions?.headerTextColor ?? '#000000'
                                }
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'headerTextColor',
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Background
                              </label>
                              <input
                                type="color"
                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                value={
                                  tableFormatOptions?.headerBackground ??
                                  '#e0e0e0'
                                }
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'headerBackground',
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Alignment
                              </label>
                              <select
                                value={
                                  tableFormatOptions?.headerAlignment ?? 'LEFT'
                                }
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'headerAlignment',
                                    e.target.value,
                                  )
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="LEFT">Left</option>
                                <option value="CENTER">Center</option>
                                <option value="RIGHT">Right</option>
                                <option value="DECIMAL">Decimal</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Cell Padding and Backgrounds - 2x2 Grid */}
                        {/* Cell Padding */}
                        <div>
                          <LinkedSpacingControl
                            title="Cell Padding"
                            valueRoot={
                              // Calculate root as average of all paddings, or use 0 if all are 0
                              (() => {
                                const left = Number(
                                  tableFormatOptions?.leftPadding ?? 0,
                                );
                                const right = Number(
                                  tableFormatOptions?.rightPadding ?? 0,
                                );
                                const top = Number(
                                  tableFormatOptions?.topPadding ?? 0,
                                );
                                const bottom = Number(
                                  tableFormatOptions?.bottomPadding ?? 0,
                                );
                                const avg = (left + right + top + bottom) / 4;
                                return avg > 0 ? Math.round(avg) : 0;
                              })()
                            }
                            valueTop={Number(tableFormatOptions?.topPadding ?? 0)}
                            valueRight={Number(
                              tableFormatOptions?.rightPadding ?? 0,
                            )}
                            valueBottom={Number(
                              tableFormatOptions?.bottomPadding ?? 0,
                            )}
                            valueLeft={Number(
                              tableFormatOptions?.leftPadding ?? 0,
                            )}
                            onRootChange={(v) => {
                              // When root changes, set all sides to the same value
                              handleTableStyleChange('leftPadding', v);
                              handleTableStyleChange('rightPadding', v);
                              handleTableStyleChange('topPadding', v);
                              handleTableStyleChange('bottomPadding', v);
                            }}
                            onTopChange={(v) =>
                              handleTableStyleChange('topPadding', v)
                            }
                            onRightChange={(v) =>
                              handleTableStyleChange('rightPadding', v)
                            }
                            onBottomChange={(v) =>
                              handleTableStyleChange('bottomPadding', v)
                            }
                            onLeftChange={(v) =>
                              handleTableStyleChange('leftPadding', v)
                            }
                          />
                        </div>

                        {/* Backgrounds */}
                        <div className="rounded-md border border-gray-200 p-2">
                          <div className="mb-3 text-xs font-semibold text-gray-700">
                            Backgrounds
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <div className="mb-2 flex items-center border-b border-gray-300 bg-white py-1">
                              <input
                                type="checkbox"
                                id={`showGrid-${index}`}
                                checked={tableFormatOptions?.showGrid ?? true}
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'showGrid',
                                    e.target.checked,
                                  )
                                }
                                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                              />
                              <label
                                htmlFor={`showGrid-${index}`}
                                className="ml-2 cursor-pointer text-xs text-gray-700"
                              >
                                Enable grid lines
                              </label>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Table Background
                              </label>
                              <input
                                type="color"
                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                value={
                                  tableFormatOptions?.background ?? '#ffffff'
                                }
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'background',
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Row Background 1
                              </label>
                              <input
                                type="color"
                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                value={
                                  tableFormatOptions?.rowBackgrounds?.[0] ??
                                  '#ffffff'
                                }
                                onChange={(e) => {
                                  const updated = [
                                    ...(tableFormatOptions?.rowBackgrounds ?? [
                                      '#ffffff',
                                      '#f0f0f0',
                                    ]),
                                  ];
                                  updated[0] = e.target.value;
                                  handleTableStyleChange(
                                    'rowBackgrounds',
                                    updated,
                                  );
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Row Background 2
                              </label>
                              <input
                                type="color"
                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                value={
                                  tableFormatOptions?.rowBackgrounds?.[1] ??
                                  '#f0f0f0'
                                }
                                onChange={(e) => {
                                  const updated = [
                                    ...(tableFormatOptions?.rowBackgrounds ?? [
                                      '#ffffff',
                                      '#f0f0f0',
                                    ]),
                                  ];
                                  updated[1] = e.target.value;
                                  handleTableStyleChange(
                                    'rowBackgrounds',
                                    updated,
                                  );
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-gray-600">
                                Border Radius
                              </label>
                              <input
                                type="number"
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={Number(
                                  tableFormatOptions?.borderRadius ?? 0,
                                )}
                                onChange={(e) =>
                                  handleTableStyleChange(
                                    'borderRadius',
                                    Number(e.target.value || 0),
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </PopoverPanel>
                  </Popover>
                </>
              )}

              {/* Chart Block Controls */}
              {currentTag && isChartTag(currentTag as ChartTag) && (
                <>
                  {/* Chart Styling & Appearance */}
                  <Popover className="relative">
                    <Tooltip
                      title="Spacing & Appearance"
                      arrow
                      placement="bottom"
                      slotProps={{
                        arrow: {
                          className: 'text-mainblue',
                        },
                      }}
                    >
                      <PopoverButton
                        className={`relative rounded border border-gray-200 p-2 text-gray-600 transition-colors hover:border-blue-500 hover:bg-gray-100 hover:text-blue-600 focus:outline-none ${hasChartCustomChanges()
                          ? 'border-blue-400 bg-blue-50'
                          : ''
                          }`}
                        aria-label={`Chart styling & appearance`}
                      >
                        <RxPadding size={18} />
                        {hasChartCustomChanges() && (
                          <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-white" />
                        )}
                      </PopoverButton>
                    </Tooltip>
                    <PopoverPanel className="absolute left-0 z-10 mt-2 max-h-[600px] w-[500px] overflow-y-auto rounded-md border border-gray-200 bg-white p-3 text-xs shadow-xl">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="font-semibold text-gray-600">
                          Styling & Appearance
                        </div>
                        {hasChartCustomChanges() && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              resetChartStyling();
                            }}
                            className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:border-red-300 hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                            aria-label="Reset chart styling"
                            title="Reset all chart styling"
                          >
                            <FaTimes size={12} />
                            <span>Clear all</span>
                          </button>
                        )}
                      </div>

                      {/* Chart Basic Styling */}
                      <div className="mb-3 border-b border-gray-200 pb-3">
                        <div className="mb-2 text-sm font-semibold text-gray-700">
                          Basic Styling
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Title</span>
                            <input
                              type="text"
                              className="ml-2 w-32 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.title ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange('title', e.target.value)
                              }
                              placeholder="Chart Title"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Font Size</span>
                            <input
                              type="number"
                              className="ml-2 w-20 rounded border border-gray-300 p-1 text-[12px]"
                              value={Number(chartFormatOptions?.fontSize ?? 12)}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'fontSize',
                                  Number(e.target.value || 12),
                                )
                              }
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">
                              Background Color
                            </span>
                            <input
                              type="text"
                              className="ml-2 w-32 rounded border border-gray-300 p-1 text-[12px]"
                              value={
                                chartFormatOptions?.backgroundColor ??
                                'rgba(0,0,0,0)'
                              }
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'backgroundColor',
                                  e.target.value,
                                )
                              }
                              placeholder="rgba(0,0,0,0)"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Color</span>
                            <input
                              type="text"
                              className="ml-2 w-32 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.color ?? '#000000'}
                              onChange={(e) =>
                                handleChartStyleChange('color', e.target.value)
                              }
                              placeholder="#000000"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Bar Chart Specific Options */}
                      {currentTag?.startsWith('bar') && (
                        <div className="mb-3 border-b border-gray-200 pb-3">
                          <div className="mb-2 text-sm font-semibold text-gray-700">
                            Bar Chart Options
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-600">Bar Mode</span>
                              <select
                                value={chartFormatOptions?.barmode ?? 'group'}
                                onChange={(e) =>
                                  handleChartStyleChange(
                                    'barmode',
                                    e.target.value,
                                  )
                                }
                                className="ml-2 w-24 rounded border border-gray-300 p-1 text-[12px]"
                              >
                                <option value="group">Group</option>
                                <option value="stack">Stack</option>
                                <option value="overlay">Overlay</option>
                              </select>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-600">Horizontal</span>
                              <input
                                type="checkbox"
                                checked={chartFormatOptions?.horizontal ?? false}
                                onChange={(e) =>
                                  handleChartStyleChange(
                                    'horizontal',
                                    e.target.checked,
                                  )
                                }
                                className="ml-2"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Legend Options */}
                      <div className="mb-3 border-b border-gray-200 pb-3">
                        <div className="mb-2 text-sm font-semibold text-gray-700">
                          Legend
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Show Legend</span>
                            <input
                              type="checkbox"
                              checked={chartFormatOptions?.showLegend !== false}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'showLegend',
                                  e.target.checked,
                                )
                              }
                              className="ml-2"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Legend Position</span>
                            <select
                              value={chartFormatOptions?.legendPosition ?? 'top'}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'legendPosition',
                                  e.target.value,
                                )
                              }
                              className="ml-2 w-24 rounded border border-gray-300 p-1 text-[12px]"
                            >
                              <option value="top">Top</option>
                              <option value="bottom">Bottom</option>
                              <option value="left">Left</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Grid Options */}
                      <div className="mb-3 border-b border-gray-200 pb-3">
                        <div className="mb-2 text-sm font-semibold text-gray-700">
                          Grid
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Show Grid X</span>
                            <input
                              type="checkbox"
                              checked={chartFormatOptions?.showGridX !== false}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'showGridX',
                                  e.target.checked,
                                )
                              }
                              className="ml-2"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Show Grid Y</span>
                            <input
                              type="checkbox"
                              checked={chartFormatOptions?.showGridY !== false}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'showGridY',
                                  e.target.checked,
                                )
                              }
                              className="ml-2"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Axis Titles */}
                      <div className="mb-3 border-b border-gray-200 pb-3">
                        <div className="mb-2 text-sm font-semibold text-gray-700">
                          Axis Titles
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">X-Axis Title</span>
                            <input
                              type="text"
                              className="ml-2 w-32 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.xaxisTitle ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'xaxisTitle',
                                  e.target.value,
                                )
                              }
                              placeholder="X-Axis Title"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Y-Axis Title</span>
                            <input
                              type="text"
                              className="ml-2 w-32 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.yaxisTitle ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'yaxisTitle',
                                  e.target.value,
                                )
                              }
                              placeholder="Y-Axis Title"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Axis Tick Format */}
                      <div className="mb-3">
                        <div className="mb-2 text-sm font-semibold text-gray-700">
                          Axis Tick Format
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">X-Axis Format</span>
                            <input
                              type="text"
                              className="ml-2 w-24 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.xaxisTickformat ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'xaxisTickformat',
                                  e.target.value,
                                )
                              }
                              placeholder="Format"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">X-Axis Suffix</span>
                            <input
                              type="text"
                              className="ml-2 w-24 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.xaxisTicksuffix ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'xaxisTicksuffix',
                                  e.target.value,
                                )
                              }
                              placeholder="Suffix"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Y-Axis Format</span>
                            <input
                              type="text"
                              className="ml-2 w-24 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.yaxisTickformat ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'yaxisTickformat',
                                  e.target.value,
                                )
                              }
                              placeholder="Format"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-600">Y-Axis Suffix</span>
                            <input
                              type="text"
                              className="ml-2 w-24 rounded border border-gray-300 p-1 text-[12px]"
                              value={chartFormatOptions?.yaxisTicksuffix ?? ''}
                              onChange={(e) =>
                                handleChartStyleChange(
                                  'yaxisTicksuffix',
                                  e.target.value,
                                )
                              }
                              placeholder="Suffix"
                            />
                          </div>
                        </div>
                      </div>
                    </PopoverPanel>
                  </Popover>
                </>
              )}

              {/* SRI Block Controls */}
              {(currentTag === 'sri_chart') && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">
                      Primary Color:
                    </label>
                    <input
                      type="color"
                      className="border-gray-30 h-8 w-12 cursor-pointer rounded border"
                      value={(formatOptions as any)?.color ?? '#22c55e'}
                      onChange={(e) => handleStyleChange('color', e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Accent Color:</label>
                    <input
                      type="color"
                      className="h-8 w-12 cursor-pointer rounded border border-gray-300"
                      value={(formatOptions as any)?.backgroundColor ?? '#16a34a'}
                      onChange={(e) =>
                        handleStyleChange('backgroundColor', e.target.value)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Content Area */}
        <div className={currentElement?.template_id ? 'pointer-events-none' : undefined}>
          {children}
        </div>
      </div>
      {!isComplete && (
        <AddBlockChooser
          onChoose={(type) =>
            addField(
              type,
              index + 1,
              currentLayout && currentFixedWidth && currentFixedWidth.length > 0
                ? currentLayout
                : undefined,
            )
          }
        />
      )}
    </div>
  );
};

export default BlockInputWrapper;
